// Tiny helpers used by both BlogPage + BlogPostPage to keep the renderer DRY.

// Renders a paragraph that may contain inline **bold** and [link](url) markdown.
// Intentionally limited — we control the source content so we do not need a
// full markdown parser.
import React from 'react';

export const renderInline = (text) => {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  // Process [text](url) links first
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;
  while (remaining.length > 0) {
    const link = remaining.match(linkRe);
    const bold = remaining.match(boldRe);
    // Pick whichever comes first
    let chosen = null;
    if (link && (!bold || link.index < bold.index)) chosen = { type: 'link', m: link };
    else if (bold) chosen = { type: 'bold', m: bold };
    if (!chosen) {
      parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }
    const before = remaining.slice(0, chosen.m.index);
    if (before) parts.push(<React.Fragment key={key++}>{before}</React.Fragment>);
    if (chosen.type === 'link') {
      parts.push(
        <a key={key++} href={chosen.m[2]} className="text-[#1A4D2E] underline hover:text-[#143d24]">
          {chosen.m[1]}
        </a>
      );
    } else {
      parts.push(<strong key={key++}>{chosen.m[1]}</strong>);
    }
    remaining = remaining.slice(chosen.m.index + chosen.m[0].length);
  }
  return parts;
};

// Update page <head> for SEO (title + meta tags) — works for crawlers that
// execute JS (Google, Bing). For pure social-share scrapers like Slack/WhatsApp
// the static index.html OG tags are the fallback.
export const useSeo = ({ title, description, image, canonical, structuredData }) => {
  React.useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [, key, name] = selector.match(/meta\[(name|property)="([^"]+)"\]/) || [];
        if (key && name) el.setAttribute(key, name);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', image);

    // Canonical link
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Article JSON-LD (only for blog posts)
    let ld = document.head.querySelector('script#blog-structured-data');
    if (structuredData) {
      if (!ld) {
        ld = document.createElement('script');
        ld.id = 'blog-structured-data';
        ld.type = 'application/ld+json';
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify(structuredData);
    } else if (ld) {
      ld.remove();
    }
  }, [title, description, image, canonical, structuredData]);
};
